import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import CloseIcon from '~/assets/icons/close.svg';
import { EditBtnIcon } from '~/editor/buttons/EditBtn';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import emptyDoorImg from '~/training/sequence/templates/items/doorCard/empty.png';
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { StandaloneCardAudioPopup } from '~/training/sequence/templates/items/doorCard/CardAudioPopUp';
import microphoneSvg from '~/assets/button/mic.svg';
import { useEditorStore } from '~/shell/Store';

const StyledModal = styled(Modal)`
  border-radius: 24px !important;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px; /* reduced spacing per request */
  @media (max-height: 900px) {
    margin-bottom: 10px;
  }
  @media (max-height: 780px) {
    margin-bottom: 8px;
  }
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.7;
  }
`;
const StyledCloseIcon = styled(CloseIcon)`
  width: 20px;
  height: 20px;
`;
const Title = styled.h3`
  margin: 0;
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  @media (max-height: 900px) {
    font-size: 17px;
  }
  @media (max-height: 780px) {
    font-size: 16px;
  }
`;
const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: Raleway;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  @media (max-height: 900px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
  @media (max-height: 780px) {
    font-size: 12.5px;
    margin-bottom: 4px;
  }
`;
const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 16px;
  background: #f8f9fa;
  &:focus {
    outline: none;
    border-color: #9738ff;
    background: #fff;
  }
  &::placeholder {
    color: #999;
  }
  @media (max-height: 900px) {
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 18px;
  }
  @media (max-height: 780px) {
    padding: 8px 12px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 16px;
  background: #f8f9fa;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #9738ff;
    background: #fff;
  }
  &::placeholder {
    color: #999;
  }
  @media (max-height: 900px) {
    min-height: 120px;
    padding: 10px 14px;
    font-size: 13px;
    margin-bottom: 18px;
  }
  @media (max-height: 780px) {
    min-height: 100px;
    padding: 8px 12px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }
`;
const ImageBox = styled.div`
  width: 100%;
  height: 300px; /* taller per request */
  border: 2px dashed #d4d8dd;
  border-radius: 12px;
  margin-bottom: 20px;
  background: #fafbfc;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  @media (max-height: 900px) {
    height: 260px;
    margin-bottom: 16px;
  }
  @media (max-height: 780px) {
    height: 220px;
    margin-bottom: 12px;
  }
`;
const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const HelperText = styled.p`
  font-family: Raleway;
  font-size: 12px;
  line-height: 16px;
  color: #555;
  margin: 6px 0 10px; /* reduced bottom gap before Card Description label */
  @media (max-height: 900px) {
    margin: 4px 0 8px;
  }
  @media (max-height: 780px) {
    margin: 2px 0 6px;
  }
`;
const EditImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  margin: 0;
  padding: 5px;
  background-color: #f3ecff;
  color: #9738ff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.18s ease, color 0.18s ease;
  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
  svg path {
    fill: currentColor !important;
    transition: fill 0.18s ease;
  }
  &:hover {
    background-color: #9738ff;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }
  &.active {
    background-color: #9845fb;
    color: #fff;
    svg path {
      fill: #fff !important;
    }
  }
`;
const ButtonRow = styled.div`
  display: flex;
  margin-top: 16px;
  gap: 12px;
  flex-wrap: wrap;
`;
const PrimaryButton = styled.button`
  width: 100%;
  padding: 10px 20px;
  border-radius: 10px;
  font-family: Raleway;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #9738ff;
  color: #fff;
  letter-spacing: 0.3px;
  transition: background 0.15s ease;
  &:hover {
    background: #7a2dcc;
  }
  &:active {
    background: #5d22a3;
  }
  @media (max-height: 900px) {
    padding: 9px 18px;
    font-size: 14.5px;
  }
  @media (max-height: 780px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;
const SecondaryButton = styled.button`
  width: 100%;
  padding: 10px 20px;
  border-radius: 10px;
  font-family: Raleway;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #d5c3f5;
  background: #f3ecff;
  color: #9738ff;
  transition: background 0.15s ease, color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover {
    background: #e6d6ff;
  }
  @media (max-height: 900px) {
    padding: 9px 18px;
    font-size: 14.5px;
  }
  @media (max-height: 780px) {
    padding: 8px 16px;
    font-size: 14px;
  }
`;
const MicIcon = styled(microphoneSvg)`
  width: 18px;
  height: 18px;
`;

interface DoorEditModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string | number | undefined;
}

export const DoorEditModal: React.FC<DoorEditModalProps> = ({
  open,
  onClose,
  cardId
}) => {
  const { sequence, updateSequence, updateFile, contentEditable } =
    useEditorStore();
  const isLearner = !contentEditable;
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [selectedImageFile, setSelectedImageFile] = useState<any | undefined>();
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [showAudioPicker, setShowAudioPicker] = useState(false);
  const [audioFile, setAudioFile] = useState<any | undefined>();
  const [audioId, setAudioId] = useState<string | number | undefined>();
  const [removedAudio, setRemovedAudio] = useState(false);
  const initialImageIdRef = useRef<string | number | undefined>(undefined);
  const initialAudioIdRef = useRef<string | number | undefined>(undefined);

  const cardItem = useMemo(() => {
    if (!sequence?.items || !cardId) return undefined;
    const stack = [...sequence.items];
    while (stack.length) {
      const it: any = stack.shift();
      if (!it) break;
      if (String(it.id) === String(cardId)) return it;
      if (it.items) stack.push(...it.items);
    }
    return undefined;
  }, [sequence, cardId, open]);

  const stripHtml = (html: string | undefined): string => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  useEffect(() => {
    if (!open) return;
    if (cardItem) {
      setDescription(stripHtml(cardItem.body));
      if (cardItem.pictures && cardItem.pictures[0]) {
        setImageUrl(cardItem.pictures[0].mediaUrl);
        initialImageIdRef.current = cardItem.pictures[0].id;
      } else {
        setImageUrl(emptyDoorImg as any);
        initialImageIdRef.current = undefined;
      }
      if (cardItem.audios && cardItem.audios[0]) {
        initialAudioIdRef.current = cardItem.audios[0].id;
        setAudioId(cardItem.audios[0].id);
      } else {
        initialAudioIdRef.current = undefined;
        setAudioId(undefined);
      }
      setRemovedAudio(false);
      setAudioFile(undefined);
    }
  }, [cardItem, open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showFilePicker) setShowFilePicker(false);
        else if (showAudioPicker) setShowAudioPicker(false);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showFilePicker, showAudioPicker, onClose]);

  const sanitizeBodyForSave = (text: string) => {
    const esc = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    return esc(text).replace(/\n/g, '<br>');
  };

  const audioPlayedRef = useRef(false);
  const audioApiRef = useRef<{ stop: () => void } | null>(null);

  const handleSubmit = () => {
    if (!cardItem) {
      onClose();
      return;
    }
    if (!isLearner) {
      if (description !== undefined)
        updateSequence(
          cardItem.id,
          'body',
          sanitizeBodyForSave(description.trim())
        );
      if (
        selectedImageFile?.id &&
        selectedImageFile.id !== initialImageIdRef.current
      ) {
        updateFile(String(cardItem.id), selectedImageFile, 'CARD');
      }
      if (removedAudio && initialAudioIdRef.current) {
        updateSequence(cardItem.id, 'audio_ids', []);
      } else if (audioFile?.id && audioFile.id !== initialAudioIdRef.current) {
        updateFile(String(cardItem.id), audioFile, 'audios');
      }
    }
    audioApiRef.current?.stop();
    onClose();
  };

  const handleImageSelect = (file: any) => {
    setSelectedImageFile(file);
    setImageUrl(file.mediaUrl || file.url);
    setShowFilePicker(false);
  };
  const handleAudioSelect = (file: any) => {
    setAudioFile(file);
    setAudioId(file?.id);
    setRemovedAudio(false);
    setShowAudioPicker(false);
  };
  const handleRemoveAudio = () => {
    setAudioFile(undefined);
    setAudioId(undefined);
    setRemovedAudio(true);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <Overlay open={open} opacity={0.5} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', zIndex: 9999 }}
      >
        <StyledModal
          open
          width="560px"
          horizontal="center"
          vertical="center"
          padding="20px 24px 24px"
          minHeight="640px"
        >
          <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
            <ModalHeader>
              <Title>{isLearner ? 'Door' : 'Edit Door'}</Title>
              <CloseButton onClick={onClose} aria-label="Close edit door modal">
                <StyledCloseIcon />
              </CloseButton>
            </ModalHeader>
            <Label>Card Image</Label>
            <ImageBox>
              <ImagePreview
                src={imageUrl || (emptyDoorImg as any)}
                alt="Door image"
              />
              {(audioFile?.mediaUrl ||
                (cardItem?.audios?.[0]?.mediaUrl && !removedAudio)) && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <div style={{ pointerEvents: 'auto' }}>
                    <StandaloneCardAudioPopup
                      mediaUrl={
                        audioFile?.mediaUrl || cardItem?.audios?.[0]?.mediaUrl
                      }
                      name={audioFile?.name || cardItem?.audios?.[0]?.name}
                      body={description}
                      replay={true}
                      btnSize={88}
                      showPopover={false}
                      onComplete={() => {
                        audioPlayedRef.current = true;
                      }}
                      onRef={api => (audioApiRef.current = api)}
                    />
                  </div>
                </div>
              )}
              {!isLearner && (
                <EditImageButton
                  type="button"
                  aria-label="Edit image"
                  onClick={() => setShowFilePicker(true)}
                >
                  <EditBtnIcon />
                </EditImageButton>
              )}
            </ImageBox>
            {!isLearner && (
              <HelperText>
                Upload files up to 20 MB, resolution should be larger than
                1000x1000 px. Accepted file formats: JPG, PNG, TIFF, GIF
              </HelperText>
            )}
            <Label htmlFor="door-description">Card Description</Label>
            {isLearner ? (
              <div
                style={{
                  fontFamily: 'Raleway',
                  fontSize: 14,
                  lineHeight: '20px',
                  marginBottom: 24
                }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              <TextArea
                id="door-description"
                value={description}
                maxLength={1200}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter description"
              />
            )}

            <ButtonRow style={{ marginTop: 0, flexDirection: 'column' }}>
              {!isLearner &&
                ((audioFile || initialAudioIdRef.current) && !removedAudio ? (
                  <SecondaryButton type="button" onClick={handleRemoveAudio}>
                    Remove Audio
                  </SecondaryButton>
                ) : (
                  <SecondaryButton
                    type="button"
                    onClick={() => setShowAudioPicker(true)}
                  >
                    <MicIcon /> Add Audio
                  </SecondaryButton>
                ))}
              <PrimaryButton
                type="button"
                aria-label={isLearner ? 'Got It' : 'Submit'}
                onClick={handleSubmit}
                disabled={
                  isLearner &&
                  !!cardItem?.audios?.[0]?.mediaUrl &&
                  !audioPlayedRef.current
                }
              >
                {isLearner ? 'Got It' : 'Submit'}
              </PrimaryButton>
            </ButtonRow>
          </div>
        </StyledModal>
        {showFilePicker &&
          ReactDOM.createPortal(
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 30000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div
                onClick={() => setShowFilePicker(false)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.35)'
                }}
              />
              <div
                style={{
                  position: 'relative',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  zIndex: 30001,
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  overflow: 'hidden'
                }}
              >
                <FilePickerV2
                  updateFile={handleImageSelect}
                  type="image"
                  fileElRef={undefined as any}
                  onClose={() => setShowFilePicker(false)}
                />
              </div>
            </div>,
            document.body
          )}
        {showAudioPicker &&
          ReactDOM.createPortal(
            <NarrationPopup
              onClose={() => setShowAudioPicker(false)}
              skipUpdate={true}
              onSelectCallback={handleAudioSelect}
              defaultText={description || undefined}
              id={audioId?.toString()}
              skipSequenceNarration={true}
            />,
            document.body
          )}
      </div>
    </Overlay>,
    document.body
  );
};

export default DoorEditModal;
